import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { NewsAlertScheduleFrequency } from "@/data-access/news-alerts/queries/news-alerts-queries";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import {
  createScheduledBriefingActions,
  type ScheduledBriefingActions,
} from "./scheduled-briefing.actions";

export type ScheduledBriefingDetails = {
  title: string;
  description: string;
  showDescription: boolean;
  prompt: string;
  hasUserPrompt: boolean;
};

export const scheduledBriefingDetailsInitialState: ScheduledBriefingDetails = {
  title: "",
  description: "",
  showDescription: false,
  prompt: "",
  hasUserPrompt: false,
};

export interface ScheduledBriefingInvalidRecipient {
  id: string;
  value: string;
}

export type ScheduledBriefingRecipients = {
  validEmails: string[];
  invalidEmails: ScheduledBriefingInvalidRecipient[];
  inputValue: string;
};

export const scheduledBriefingRecipientsInitialState: ScheduledBriefingRecipients =
  {
    validEmails: [],
    invalidEmails: [],
    inputValue: "",
  };

export type ScheduledBriefingLogo = {
  logoFile: File | null;
  previewUrl: string | null;
  fileName: string | null;
  /** Edit flow: user removed an existing logo (send `input.logo: null` on save). */
  isRemoved: boolean;
};

export const scheduledBriefingLogoInitialState: ScheduledBriefingLogo = {
  logoFile: null,
  previewUrl: null,
  fileName: null,
  isRemoved: false,
};

export type ScheduledBriefingSchedule = {
  scheduleFrequency: NewsAlertScheduleFrequency;
  scheduleTime: string;
  timezone: TimezoneChoices | "";
};

export const scheduledBriefingScheduleInitialState: ScheduledBriefingSchedule =
  {
    scheduleFrequency: "DAILY",
    scheduleTime: "8:00 AM",
    timezone: "",
  };

export type ScheduledBriefingModalState = {
  isCreateScheduledBriefingModalOpen: boolean;
  isCreatingScheduledBriefing: boolean;
  isGeneratingScheduledBriefingPreview: boolean;
  scheduledBriefingDetails: ScheduledBriefingDetails;
  scheduledBriefingSchedule: ScheduledBriefingSchedule;
  scheduledBriefingRecipients: ScheduledBriefingRecipients;
  scheduledBriefingLogo: ScheduledBriefingLogo;
  editingScheduledBriefingId: string | null;
  isFetchingScheduledBriefingById: boolean;
  isUpdatingScheduledBriefing: boolean;
  isDeletingScheduledBriefing: boolean;
  isDeleteScheduledBriefingModalOpen: boolean;
  deletingScheduledBriefingId: string | null;
  deletingScheduledBriefingName: string;
};

export const scheduledBriefingInitialState: ScheduledBriefingModalState = {
  isCreateScheduledBriefingModalOpen: false,
  isCreatingScheduledBriefing: false,
  isGeneratingScheduledBriefingPreview: false,
  scheduledBriefingDetails: scheduledBriefingDetailsInitialState,
  scheduledBriefingSchedule: scheduledBriefingScheduleInitialState,
  scheduledBriefingRecipients: scheduledBriefingRecipientsInitialState,
  scheduledBriefingLogo: scheduledBriefingLogoInitialState,
  editingScheduledBriefingId: null,
  isFetchingScheduledBriefingById: false,
  isUpdatingScheduledBriefing: false,
  isDeletingScheduledBriefing: false,
  isDeleteScheduledBriefingModalOpen: false,
  deletingScheduledBriefingId: null,
  deletingScheduledBriefingName: "",
};

export type ScheduledBriefingSlice = ScheduledBriefingModalState &
  ScheduledBriefingActions;

const createScheduledBriefingSlice: StateCreator<
  ScheduledBriefingSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  ...scheduledBriefingInitialState,
  ...createScheduledBriefingActions(set, get, api),
});

const useScheduledBriefingStore = create<ScheduledBriefingSlice>()(
  devtools(createScheduledBriefingSlice, {
    name: "scheduled-briefing-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useScheduledBriefingStore;
